
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useBanCheck() {
  const { currentUser } = useAuth();
  const [isBanned, setIsBanned] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [banInfo, setBanInfo] = useState<{
    reason: string;
    expires_at: string | null;
  } | null>(null);

  useEffect(() => {
    const checkBanStatus = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user is banned
        const { data: isBannedStatus, error: bannedStatusError } = await supabase
          .rpc('is_user_banned', { user_id_param: currentUser.id });
          
        if (bannedStatusError) throw bannedStatusError;
        
        setIsBanned(!!isBannedStatus);
        
        // If banned, get the reason and expiry date
        if (isBannedStatus) {
          const { data: banData, error: banError } = await supabase
            .from('banned_users')
            .select('reason, expires_at')
            .eq('user_id', currentUser.id)
            .eq('status', 'active')
            .order('banned_at', { ascending: false })
            .limit(1)
            .single();
            
          if (!banError && banData) {
            setBanInfo({
              reason: banData.reason,
              expires_at: banData.expires_at
            });
          }
        }
      } catch (error) {
        console.error('Error checking ban status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkBanStatus();
  }, [currentUser]);

  return { isBanned, isLoading, banInfo };
}
